import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { formatDate, formatIntegerOrDash, formatRupees } from '@utils/format';
import { RowActions, type MarketPriceRowActions } from './components/cells/RowActions';
import { SourceBadge } from './components/cells/SourceBadge';
import type { MarketPrice } from './schema';

const formatRupeesCell = (
  p: ValueFormatterParams<MarketPrice, number | null>,
): string => formatRupees(p.value);

const formatNumberOrDash = (
  p: ValueFormatterParams<MarketPrice, number | null>,
): string => formatIntegerOrDash(p.value);

const formatDateCell = (
  p: ValueFormatterParams<MarketPrice, string>,
): string => formatDate(p.value);

// `colId` is the API's sortBy key (date|min|modal|max|arrivals). When the
// editor clicks a sortable header, MarketPricesGrid reads the colId from
// the column state and sends it as `sortBy` to the API.
export function buildMarketPricesColumns(
  actions: MarketPriceRowActions,
): ColDef<MarketPrice>[] {
  return [
    {
      colId: 'date',
      field: 'priceDate',
      headerName: 'Date',
      width: 130,
      pinned: 'left',
      lockPinned: true,
      sortable: true,
      valueFormatter: formatDateCell,
    },
    {
      field: 'cropName',
      headerName: 'Crop',
      width: 160,
    },
    {
      field: 'varietyName',
      headerName: 'Variety',
      width: 160,
    },
    {
      field: 'apmcName',
      headerName: 'Mandi',
      flex: 1,
      minWidth: 200,
    },
    {
      colId: 'min',
      field: 'minPrice',
      headerName: 'Min',
      width: 110,
      type: 'rightAligned',
      sortable: true,
      valueFormatter: formatRupeesCell,
    },
    {
      colId: 'modal',
      field: 'modalPrice',
      headerName: 'Modal',
      width: 120,
      type: 'rightAligned',
      sortable: true,
      valueFormatter: formatRupeesCell,
    },
    {
      colId: 'max',
      field: 'maxPrice',
      headerName: 'Max',
      width: 110,
      type: 'rightAligned',
      sortable: true,
      valueFormatter: formatRupeesCell,
    },
    {
      colId: 'arrivals',
      field: 'commodityArrivals',
      headerName: 'Arrivals',
      width: 110,
      type: 'rightAligned',
      sortable: true,
      valueFormatter: formatNumberOrDash,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 80,
    },
    {
      field: 'dataSource',
      headerName: 'Source',
      width: 200,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<MarketPrice>) => <SourceBadge {...p} />,
    },
    {
      headerName: '',
      width: 60,
      pinned: 'right',
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (p: ICellRendererParams<MarketPrice>) =>
        p.data ? <RowActions row={p.data} {...actions} /> : null,
    },
  ];
}
